import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const approaches = [
  {
    title: 'Operator-level thinking',
    description: "We're not consultants. We think like operators. We've built companies, scaled systems, and removed bottlenecks firsthand. Our frameworks come from real execution, not theory."
  },
  {
    title: 'Framework-driven analysis',
    description: 'We apply proven frameworks systematically to identify bottlenecks. This structured approach ensures we find the highest-impact constraint, not just the most obvious one.'
  },
  {
    title: 'Founder-focused partnership',
    description: 'We work directly with founders who understand their business deeply. Our role is to bring external perspective, proven frameworks, and execution support to identify and remove bottlenecks.'
  },
  {
    title: 'Measurement-obsessed',
    description: "Every bottleneck removal is measured. We track before and after metrics to validate impact. If a change doesn't unlock growth, we iterate until it does."
  }
];

interface ApproachCardProps {
  title: string;
  description: string;
  index: number;
}

const ApproachCard = ({ title, description, index }: ApproachCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: cardProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "center center"]
  });

  const cardOpacity = useTransform(cardProgress, [0, 0.5], [0, 1]);
  const cardY = useTransform(cardProgress, [0, 0.5], [80, 0]);
  const cardScale = useTransform(cardProgress, [0, 0.5], [0.95, 1]);

  return (
    <motion.div
      ref={cardRef}
      style={{ opacity: cardOpacity, y: cardY, scale: cardScale }}
      className="bg-dark-card border border-dark-border rounded-2xl md:rounded-3xl p-8 md:p-10 lg:p-12 hover:border-accent-blue/30 transition-all duration-300"
    >
      <div className="flex items-start gap-4 mb-4 md:mb-6">
        <div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full bg-accent-blue/10 border border-accent-blue/20 flex items-center justify-center">
          <span className="text-accent-blue font-bold text-sm md:text-base">{index + 1}</span>
        </div>
        <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white leading-tight pt-1">
          {title}
        </h3>
      </div>
      <p className="text-base md:text-lg text-gray-400 leading-relaxed">
        {description}
      </p>
    </motion.div>
  );
};

const Approach = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const titleOpacity = useTransform(scrollYProgress, [0.1, 0.25], [0, 1]);
  const titleY = useTransform(scrollYProgress, [0.1, 0.25], [60, 0]);
  const glowIntensity = useTransform(scrollYProgress, [0.15, 0.4, 0.7], [0.03, 0.12, 0.03]);

  return (
    <section ref={containerRef} className="relative bg-dark-light py-32 md:py-48 overflow-hidden">
      {/* Continuous Background Grid */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(0, 102, 255, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 102, 255, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />
      </div>

      {/* Dynamic Background Glows */}
      <motion.div
        className="absolute top-1/3 left-1/3 w-[500px] h-[500px] bg-accent-blue rounded-full blur-3xl pointer-events-none"
        style={{ opacity: glowIntensity }}
      />
      <motion.div
        className="absolute bottom-1/3 right-1/3 w-[500px] h-[500px] bg-accent-blue rounded-full blur-3xl pointer-events-none"
        style={{ opacity: glowIntensity }}
      />

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-8">
        <motion.h2
          style={{ opacity: titleOpacity, y: titleY }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold text-white text-center mb-12 md:mb-20"
        >
          Our Approach
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {approaches.map((approach, index) => (
            <ApproachCard
              key={index}
              index={index}
              title={approach.title}
              description={approach.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Approach;
